<template>
	<Transition
		name="slide-in"
		@after-leave="$emit('closed')"
	>
		<div
			v-if="show"
			class="success-toast"
		>
			<div class="success-toast-inner">
				<div class="success-toast-icon">
					<svg
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2.5"
							d="M5 13l4 4L19 7"
						/>
					</svg>
				</div>
				<div>
					<p class="success-toast-title">Success!</p>
					<p class="success-toast-message">{{ message }}</p>
				</div>
				<button
					type="button"
					class="success-toast-close"
					aria-label="Close"
					@click="close"
				>
					<svg
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>
			</div>
		</div>
	</Transition>
</template>

<script setup>
const props = defineProps({
	message: {
		type: String,
		default: "You have successfully unsubscribed"
	},
	autoDismiss: {
		type: Number,
		default: 10000 // 10 seconds
	}
});

defineEmits(["closed"]);

const show = ref(false);
let dismissTimer = null;

const close = () => {
	show.value = false;
	if (dismissTimer) {
		clearTimeout(dismissTimer);
		dismissTimer = null;
	}
};

const open = () => {
	show.value = true;

	// Auto-dismiss after specified time
	if (props.autoDismiss > 0) {
		dismissTimer = setTimeout(() => {
			close();
		}, props.autoDismiss);
	}
};

// Expose methods
defineExpose({
	open,
	close
});

onBeforeUnmount(() => {
	if (dismissTimer) {
		clearTimeout(dismissTimer);
	}
});
</script>

<style scoped>
.success-toast {
	position: fixed;
	top: 92px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 10000;
	width: 100%;
	max-width: 560px;
	padding: 0 16px;
}

.success-toast-inner {
	display: flex;
	align-items: center;
	gap: 16px;
	background-color: #01b885;
	color: white;
	padding: 20px 24px;
	border-radius: 12px;
	box-shadow:
		0 10px 25px rgba(1, 184, 133, 0.3),
		0 4px 10px rgba(0, 0, 0, 0.1);
}

.success-toast-icon {
	flex-shrink: 0;
	width: 40px;
	height: 40px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		width: 24px;
		height: 24px;
	}
}

.success-toast-title {
	font-size: 18px;
	font-weight: 700;
	line-height: 1.2;
}

.success-toast-message {
	font-size: 15px;
	margin-top: 2px;
	opacity: 0.9;
}

.success-toast-close {
	flex-shrink: 0;
	margin-left: auto;
	padding: 4px;
	border-radius: 6px;
	background: transparent;
	border: none;
	color: white;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.7;
	}

	svg {
		width: 22px;
		height: 22px;
	}
}

.slide-in-enter-active {
	animation: fade-scale-in 0.4s ease-out;
}

.slide-in-leave-active {
	animation: fade-scale-out 0.25s ease-in;
}

@keyframes fade-scale-in {
	from {
		transform: translateX(-50%) translateY(-12px) scale(0.95);
		opacity: 0;
	}
	to {
		transform: translateX(-50%) translateY(0) scale(1);
		opacity: 1;
	}
}

@keyframes fade-scale-out {
	from {
		transform: translateX(-50%) translateY(0) scale(1);
		opacity: 1;
	}
	to {
		transform: translateX(-50%) translateY(-12px) scale(0.95);
		opacity: 0;
	}
}
</style>
